import angular from 'angular';
import sanitize from 'angular-sanitize';
import angularUI from 'angular-ui-bootstrap';
import statusMessage from '../status-message';
import i18n from '../i18n';
import spinnerButton from '../spinner-button';

import modalWindowService from './modal-window-service';
import modalWindowBodyDirective from './modal-window-body-directive';
import overrideModalWindowTemplate from './templates/window.tpl.html';
import modalWindowDecorator from './modal-window-decorator';

function run($templateCache) {
  /*
  Overide angular-ui bootstrap's modal window template for maxwidth functionality
  */
  $templateCache.put('template/modal/window.html', overrideModalWindowTemplate);
}
run.$inject = ['$templateCache'];

/**
 * @ngdoc module
 * @name akamai.components.modal-window
 *
 * @guideline https://design.akamai.com/components/modal-window/
 *
 * @description
 * Modal window is a type of pop up dialog box overlaid on the current page.
 * It contains a transparent background and a content panel with interactive elements.
 * Modal windows are triggered by user action on the parent page and require user interaction
 * before returning to previous workflow. Content to be presented in modal window is limited to
 * one content panel. Typical usages of modal windows include collecting user input
 * or deliberately interrupting a task flow to provide or collect information.
 *
 * @example index.js
 * function MyController(modalWindow) {
 *   modalWindow.open({
 *    template: '...'
 *   }).result
 *    .then(() => {
 *      // user clicked "ok"
 *    })
 *    .catch(() => {
 *      // user clicked "cancel"
 *    });
 * }
 *
 * @example setOnSubmitExample.js
 *
 * function modalWindowController($scope, $q, $timeout) {
 *   var times = 0;
 *
 *   $scope.setOnSubmit(function(){
 *     var deferral = $q.defer();
 *
 *     $timeout(function() {
 *       if (times % 2 === 0) {
 *         deferral.reject();
 *       } else {
 *         deferral.resolve();
 *       }
 *       times++;
 *       }, 3000);
 *
 *       return deferral.promise;
 *     });
 * }
 *
 * @example injectModalInstanceExample.js
 *
 * function modalWindowController($modalInstance, $http) {
 *
 *   $http.get('someBadUrl.json').then(function(result){
 *    if(!result.data.success){
 *      //deal with error and close window
 *      $modalInstance.close();
 *    }
 *   });
 *
 * }
 */
export default angular.module('akamai.components.modal-window', [
  angularUI,
  sanitize,
  statusMessage.name,
  i18n.name,
  spinnerButton.name
])

.run(run)
.config(modalWindowDecorator)

/**
 * @ngdoc service
 * @name modalWindow
 * @description Provides a method to open new modal window instances.
 *
 */

/**
 * @ngdoc method
 *
 * @name modalWindow#open
 *
 * @description Opens a new modal window.
 *
 * @param {Object} options A hash with the options specified below.
 *
 * @param {constructor|string} [options.controller] A controller for the modal window
 * instance that can initialize scope.
 *
 * @param {string} [options.controllerAs] an alternative to the controller-as syntax,
 * matching the API of directive definitions. Requires the controller option to be
 * provided as well
 *
 * @param {string} [options.component] A string reference to the component to be rendered that is
 * registered with Angular's compiler. If using a directive, the directive must have restrict: 'E'
 * and a template or templateUrl set.
 *
 * It supports these bindings:
 *     setOnSubmit - See description in scope object below.
 *
 *     enableSubmit - Calling this function will enable the submit button.
 *
 *     disableSubmit - Calling this function will disable the submit button.
 *
 *     resolve - An object of the modal resolve values.
 *
 * @param {Object} [options.resolve] Members that will be resolved and passed to the controller
 * as locals.
 *
 * @param {boolean} [options.hideSubmit=false] A flag to hide the submit button and only allow
 * the modal window to be dismissed.
 *
 * @param {Scope} [options.scope=$rootScope] A scope instance to use for the modal window
 * body content.
 *
 * @param {function(): Promise} [options.scope.setOnSubmit] This function will be called
 * when the user clicks the submit button. If the promise is resolved, the modal window will
 * close and the `successMessage` will be displayed. If the promise is rejected, the modal will
 * evaluate the arguments of the rejection to check if they follow a string value (key or already
 * translated value) or an object containing an i18nKey property or none of the above (in which
 * case the default options.errorMessage prevails). The modal window will remain open and the
 * `errorMessage` (as determined dynamically by the rejection) will be displayed. If a promise is
 * not returned by `setOnSubmit` the modal window will close.
 *
 * @param {function()} [options.scope.disableSubmit] Calling this function will disable the
 * submit button.
 *
 * @param {function()} [options.scope.enableSubmit] Calling this function will enable the
 * submit button.
 *
 * @param {string} options.template An inline template to render within the body of the
 * modal window.
 *
 * @param {string} options.templateUrl A URL referencing a template to render within the body of
 * the modal window.
 *
 * @param {boolean} [options.doNotShowMessage] If true, the `successMessage` will not be shown
 *
 * @param {boolean} [options.showFullscreenToggle] Allows for the modal window to be
 * maximized and restored via the user clicking/toggling the full screen/restore icon
 *
 * @param {boolean} [options.showCloseButton=true] Allows for the modal window to have
 * a close button in the top right corner.
 *
 * @param {boolean} [options.animation=true] Set to false to disable animations on new
 * modal windows/backdrops. Does not toggle animations for modal windows/backdrops
 * that are already displayed.
 *
 * @param {boolean} [options.keyboard=true] Indicates whether the dialog should be closable
 * by hitting the ESC key.
 *
 * @param {string} [options.backdropClass] Additional CSS class(es) to be added to a modal window
 * backdrop template.
 *
 * @param {string} [options.windowClass] Additional CSS class(es) to be added to a modal
 * window template
 *
 * @param {string} [options.openedClass=modal-open] Class added to the `body` element
 * when the modal window is opened. Defaults to `modal-open`
 *
 * @param {string} [options.icon] Add a header icon to the modal window. Supply the icon class
 * name to show that icon to the left of the modal header.
 *
 * @param {string} size Optional suffix of modal window class. The value used is appended to
 * the `modal-` class, i.e. a value of `sm` gives `modal-sm`
 *
 * @param {string|TranslateKey} [options.title=Modal Window] A title for the modal window
 *
 * @param {Object} [titleValues] If `TranslateKey` is used for `title`, this object
 * hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 * @param {string|TranslateKey} [options.submitLabel=Save] A label for the submit button
 *
 * @param {Object} [submitLabelValues] If `TranslateKey` is used for `submitLabel`, this object
 * hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 * @param {string|TranslateKey} [options.cancelLabel=Cancel] A label for the cancel button
 *
 * @param {Object} [cancelLabelValues] If `TranslateKey` is used for `cancelLabel`, this object
 * hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 * @param {number} [options.successMessageTimeout=2000]
 * The time (in ms) before the status message removes
 * itself from being shown. *Note: Use timeout = 0 to force the user to close the message.*
 *
 * @param {string|TranslateKey} [options.successMessage=The action has been completed.] The
 * message shown in a [status-message](status-message/) when the modal-window submit process
 * completes successfully
 *
 * @param {Object} [successMessageValues] If `TranslateKey` is used for `successMessage`, this
 * object hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 * @param {string|TranslateKey} [options.errorMessage=The action can't be completed.] The default
 * error message shown at the top of the modal-window, when an error occurs during the submit
 * process.  Note: this message is overridden in two cases:
 * <ol>
 * <li> the promise is rejected with a string. </li>
 * <li> the promise is rejected with an object that contains i18nKey and optionally i18nKeyValues
 * properties within the object. </li>
 * </ol>
 * @param {Object} [errorMessageValues] If `TranslateKey` is used for `errorMessage`, this
 * object hash will be used for translations with variable interpolation. See
 * [angular-translate's translate-value support](https://angular-translate.github.io/docs/#/guide/06_variable-replacement).
 *
 *
 * @return {Object} An instance of the modal window with the following properties:
 *
 * - `close` (function) A method to close the modal window
 *   that accepts a result as an argument.
 *
 * - `dismiss` (function) A method to dismiss the modal
 *   window, rejecting the `result` promise.
 *
 * - `result` (Promise) A promise representing the result
 *   when the modal window is closed.
 *
 */
  .service('modalWindow', modalWindowService)

/**
 * @name akamModalWindowBody
 * @description Provides directive to display content body section in html.
 */
  .directive('akamModalWindowBody', modalWindowBodyDirective);
